<template>
  <div>
    <!-- Expansion panels — accordion, first panel open -->

    <div class="text-center">
      <h3 class="mt-3 mb-0 font-weight-bold">
        List of Academic Services and Working Hours
      </h3>
      <p class="mt-0 mb-5 text-muted">
        Expertise of MUIC lecturers serving organizations outside MUIC
      </p>
    </div>

    <v-expansion-panels v-model="openPanel" accordion flat>
      <v-expansion-panel
        v-for="(group, idx) in chartGroups"
        :key="group.name"
        class="mb-4 chart-panel"
      >
        <v-expansion-panel-header class="panel-header">
          <div class="d-flex align-center">
            <div class="panel-icon-wrap" :class="panelIconClass(idx)">
              <v-icon :color="panelIconColor(idx)" size="22">{{
                group.icon
              }}</v-icon>
            </div>
            <span class="panel-title ml-4">{{ group.name }}</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <apexchart
            width="100%"
            :height="group.chartHeight"
            type="bar"
            :options="group.chartOptions"
            :series="group.series"
          ></apexchart>

          <!-- Legend list -->
          <div class="legend-list">
            <div
              v-for="item in group.legendItems"
              :key="item.id"
              class="legend-row"
            >
              <div class="d-flex align-center" style="flex: 1; min-width: 0">
                <div class="legend-abbr-box">
                  <span class="legend-abbr-text">{{ item.abbreviation }}</span>
                </div>
                <div class="legend-detail">
                  <span class="legend-detail-name">{{ item.detail }}</span>
                  <span class="legend-detail-sub text--disabled"
                    >#{{ item.abbreviation }}</span
                  >
                </div>
              </div>
              <div class="legend-value-col">
                <span class="legend-point">{{
                  item.point.toLocaleString()
                }}</span>
                <span class="legend-point-label text--disabled">pts</span>
              </div>
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
// Fixed display order for name_type groups
const NAME_TYPE_ORDER = [
  "Service-Oriented activities",
  "Practice-Oriented activities",
  "Teaching-Oriented activities",
  "Others",
];

const GROUP_ICONS = {
  "Service-Oriented activities": "mdi-account-group-outline",
  "Practice-Oriented activities": "mdi-clipboard-text-outline",
  "Teaching-Oriented activities": "mdi-school-outline",
  Others: "mdi-dots-horizontal-circle-outline",
};

const GROUP_COLORS = {
  "Service-Oriented activities": {
    bg: "blue lighten-5",
    icon: "blue darken-1",
  },
  "Practice-Oriented activities": {
    bg: "green lighten-5",
    icon: "green darken-1",
  },
  "Teaching-Oriented activities": {
    bg: "orange lighten-5",
    icon: "orange darken-1",
  },
  Others: { bg: "purple lighten-5", icon: "purple darken-1" },
};

export default {
  components: {},
  props: {
    activityList: {
      type: Array,
      default: () => [],
    },
    activityTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      openPanel: undefined, // All panels closed by default
      chartGroups: [],
    };
  },
  watch: {
    activityList: {
      deep: true,
      handler() {
        this.updateCharts();
      },
    },
    activityTypes: {
      deep: true,
      handler() {
        this.updateCharts();
      },
    },
  },
  created() {
    this.updateCharts();
  },
  methods: {
    panelIconClass(idx) {
      const group = this.chartGroups[idx];
      if (!group) return "";
      const colors = GROUP_COLORS[group.name] || GROUP_COLORS["Others"];
      return colors.bg;
    },
    panelIconColor(idx) {
      const group = this.chartGroups[idx];
      if (!group) return "grey";
      const colors = GROUP_COLORS[group.name] || GROUP_COLORS["Others"];
      return colors.icon;
    },
    /**
     * Create abbreviation from detail, skipping text inside brackets e.g. [ ≥1MB & <3MB completed ]
     */
    makeAbbreviation(detail) {
      // Remove text inside [ ... ] first
      const cleaned = detail.replace(/\[.*?\]/g, "").trim();
      if (!cleaned) return "";
      const initials = cleaned
        .split(/\s+/)
        .map((word) => {
          // Find first letter character in the word
          const match = word.match(/[a-zA-Z]/);
          return match ? match[0].toUpperCase() : "";
        })
        .filter((i) => i !== ""); // Remove empty initials

      if (initials.length <= 2) return initials.join("");
      // Remove the dot as requested
      return initials.slice(0, 2).join("");
    },
    updateCharts() {
      // 1️⃣ Find unique name_type groups
      const uniqueNameTypes = [
        ...new Set(
          this.activityTypes
            .map((t) => t.name_type)
            .filter((n) => n && n !== "null")
        ),
      ];

      // 2️⃣ Sort by fixed order
      const sorted = [...uniqueNameTypes].sort((a, b) => {
        const ia = NAME_TYPE_ORDER.indexOf(a);
        const ib = NAME_TYPE_ORDER.indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      });

      // 3️⃣ Filter approved list + skip activity_type that is null / '' / 'null'
      const approvedList = this.activityList.filter(
        (a) =>
          a.status === "approve" &&
          !["", null, "null"].includes(a.activity_type)
      );

      // 4️⃣ Sum point and hour by activity_type
      const pointSumByType = {};
      const hourSumByType = {};

      approvedList.forEach((item) => {
        const typeId = item.activity_type;
        const point = Number(item.point_activity || 0);
        const hour = Number(item.hour_activity || 0);

        pointSumByType[typeId] = (pointSumByType[typeId] || 0) + point;
        hourSumByType[typeId] = (hourSumByType[typeId] || 0) + hour;
      });

      // 5️⃣ Build chartGroups — separate chart per name_type
      this.chartGroups = sorted.map((nameType) => {
        const types = this.activityTypes.filter(
          (t) => t.name_type === nameType
        );
        const abbreviations = types.map((t) => this.makeAbbreviation(t.detail));
        const categories = [...abbreviations];

        const pointData = types.map((t) => pointSumByType[t.id] || 0);
        const hourData = types.map((t) => hourSumByType[t.id] || 0);

        const chartOptions = {
          chart: {
            type: "bar",
            toolbar: {
              show: true,
              tools: {
                download: false,
                selection: true,
                zoom: true,
                zoomin: true,
                zoomout: true,
                pan: true,
                reset: true,
              },
            },
          },
          plotOptions: { bar: { horizontal: true, barHeight: "60%" } },
          xaxis: { categories: categories },
          annotations: {
            xaxis: categories.map((abbr, index) => ({
              x: index,
              borderColor: "transparent",
              label: {
                text: ``,
                style: {
                  color: "#000",
                  fontSize: "12px",
                },
              },
            })),
          },
        };

        const series = [
          { name: "Total Points (Approved)", data: pointData },
          { name: "Total Hours (Approved)", data: hourData },
        ];

        // 6️⃣ Build legend items sorted by point descending
        const legendItems = types
          .map((t, i) => ({
            id: t.id,
            detail: t.detail,
            abbreviation: abbreviations[i],
            point: pointSumByType[t.id] || 0,
            hour: hourSumByType[t.id] || 0,
          }))
          .sort((a, b) => b.point - a.point);

        // Chart height based on number of categories (60px per bar, min 200)
        const chartHeight = Math.max(200, categories.length * 60 + 80);

        return {
          name: nameType,
          icon: GROUP_ICONS[nameType] || "mdi-chart-box-multiple-outline",
          types,
          abbreviations,
          legendItems,
          chartOptions,
          chartHeight,
          series,
        };
      });
    },
  },
  mounted() {},
};
</script>

<style scoped>
.chart-panel {
  border: 1px solid #e8e8e8;
  border-radius: 12px !important;
  overflow: hidden;
}

.chart-panel::before {
  box-shadow: none !important;
}

.panel-header {
  min-height: 64px;
}

.panel-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
}

.panel-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
}

/* Legend list */
.legend-list {
  margin-top: 8px;
}

.legend-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 4px;
  border-bottom: 1px solid #f0f0f0;
}

.legend-row:last-child {
  border-bottom: none;
}

.legend-abbr-box {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 8px;
  border-radius: 10px;
  background: #f5f5f5;
}

.legend-abbr-text {
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
}

.legend-detail {
  display: flex;
  flex-direction: column;
  margin-left: 12px;
  min-width: 0;
}

.legend-detail-name {
  font-size: 13px;
  font-weight: 500;
  color: #2d3748;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.legend-detail-sub {
  font-size: 11px;
}

.legend-value-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  white-space: nowrap;
}

.legend-point {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
}

.legend-point-label {
  font-size: 11px;
}
</style>
